import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../modal/Modal';
import {
    isFocusTakenOutside,
    SCHEDULE_CONFIRM_DIALOG_Z_INDEX,
    useConfirmDialogFocusGuard,
} from '../CreateSchedule/scheduleConfirmDialog';
import { PauseGateHit } from '@/features/membershipPause/createGate';
import { formatKstDate } from '@/features/membershipPause/status';

type Props = {
    hits: PauseGateHit[];
    isResuming: boolean;
    onResume: () => void;
    onKeep: () => void;
    onCancel: () => void;
};

const describePeriod = (hit: PauseGateHit) => {
    const start = formatKstDate(hit.open.startAt, 'M/D');
    if (!hit.open.endAt) return `${start}부터 · 해제일 없음`;
    return `${start} ~ ${formatKstDate(hit.open.endAt, 'M/D')} 해제 예정`;
};

const describeResume = (hit: PauseGateHit) => {
    if (hit.isScheduled || hit.preview?.isCancel) return '풀면 정지 예정이 취소되고 만료일은 그대로예요.';
    const days = hit.preview?.days ?? 0;
    if (days <= 0) return '풀면 정지가 끝나요. 만료일은 그대로예요.';
    if (hit.preview?.expiryBefore && hit.preview.expiryAfter) {
        return `풀면 만료일이 ${days}일 늘어나요 (${formatKstDate(hit.preview.expiryBefore)} → ${formatKstDate(
            hit.preview.expiryAfter,
        )}, 예상).`;
    }
    return `풀면 만료일이 ${days}일 늘어나요(예상).`;
};

/**
 * 정지 중인 수강권으로 정지 기간 안에 수업을 만들 때의 확인 창 — 「풀고 등록 / 그대로 등록 / 취소」.
 * 세 갈래가 모두 보여야 해서 전역 2버튼 알림 대신 공용 Modal 위에 따로 그린다.
 * 두 등록 선택지가 모두 되돌리기 어려우므로 강조(주 버튼)는 덜 파괴적인 「그대로 등록」에 둔다.
 */
const ModalPauseCreateConfirm = ({ hits, isResuming, onResume, onKeep, onCancel }: Props) => {
    const hasShared = hits.some((h) => h.isShared);
    /**
     * 공용 Modal 은 처음 그릴 때 받은 onClose 를 키보드 처리기에 붙잡는다. 그대로 넘기면 해제 중에도
     * 처음의 `isResuming=false` 닫기 함수가 Escape 로 실행된다 → 늘 최신 함수를 부르는 고정 함수를 넘긴다.
     * 공용 Modal 은 호출부가 많아 고치지 않는다.
     */
    const closeRef = useRef<() => void>(() => undefined);
    closeRef.current = () => {
        if (!isResuming) onCancel();
    };
    const stableClose = useRef(() => closeRef.current()).current;

    /**
     * 맨 위 창 보장 — 날짜 확인 창(scheduleConfirmDialog)의 보호를 그대로 쓴다(교차검증 11).
     *  - 공통 알럿(10001)보다 위 층에 띄운다
     *  - 열리면 포커스를 창 자체로 옮기고, Tab 은 창 안에서만 돌며, 밖으로 나간 포커스는 되돌린다
     *  - 실행 순간 창 밖 다른 요소가 포커스를 갖고 있으면(위에 다른 창이 떴다) 해제·생성을 실행하지 않는다
     */
    const layerRef = useRef<HTMLDivElement | null>(null);
    useConfirmDialogFocusGuard(layerRef, true);
    const guarded = (action: () => void) => () => {
        if (isFocusTakenOutside(layerRef.current)) return;
        action();
    };

    return createPortal(
        <div
            ref={layerRef}
            role="dialog"
            aria-modal="true"
            aria-label="정지 중인 수강권이에요"
            tabIndex={-1}
            data-testid="pause-create-confirm-layer"
            style={{ position: 'fixed', inset: 0, zIndex: SCHEDULE_CONFIRM_DIALOG_Z_INDEX, outline: 'none' }}
        >
        <Modal whiteViewWidth={440} onClose={stableClose} isCloseOutside={false} isWhiteOverFlowScroll={false}>
            <div className="flex flex-col px-[28px] pt-[28px] pb-[24px]" onClick={(e) => e.stopPropagation()}>
                <div className="text-[18px] font-bold text-[#1F2937]">정지 중인 수강권이에요</div>
                <div className="mt-[8px] text-[14px] leading-[22px] text-[#4B5563]">
                    등록하려는 수업이 정지 기간에 들어가요. 정지를 풀고 등록할지, 정지를 그대로 두고 등록할지 골라 주세요.
                </div>
                <ul className="mt-[16px] flex max-h-[280px] flex-col gap-[10px] overflow-y-auto">
                    {hits.map((hit) => (
                        <li key={hit.key} className="rounded-[10px] border border-[#E5E7EB] bg-[#F9FAFB] px-[14px] py-[12px]">
                            <div className="text-[14px] font-semibold text-[#111827]">
                                {hit.memberNames.length ? `${hit.memberNames.map((n) => `${n}님`).join(', ')} · ` : ''}
                                {hit.passName}
                            </div>
                            <div className="mt-[4px] text-[13px] text-[#475569]">{describePeriod(hit)}</div>
                            <div className="mt-[4px] text-[13px] text-[#475569]">
                                이 등록의 수업 {hit.hitCount}회가 정지 기간에 들어가요.
                            </div>
                            <div className="mt-[4px] text-[13px] text-[#64748B]">{describeResume(hit)}</div>
                            {hit.isShared && (
                                <div className="mt-[6px] text-[13px] font-semibold text-[#B45309]">
                                    공유 수강권이에요. 풀면 소유자
                                    {hit.sharedOwnerName ? ` ${hit.sharedOwnerName}님` : ''} 수강권의 정지도 함께 풀려요.
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
                {hasShared && <div className="mt-[10px] text-[12px] text-[#6B7280]">공유 수강권은 함께 쓰는 사람 모두에게 정지가 풀려요.</div>}
                <div className="mt-[20px] flex flex-row justify-end gap-[8px]">
                    <button
                        type="button"
                        disabled={isResuming}
                        onClick={onCancel}
                        className="h-[40px] rounded-[8px] border border-[#D1D5DB] bg-white px-[16px] text-[14px] font-semibold text-[#374151] disabled:opacity-50"
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        disabled={isResuming}
                        onClick={guarded(onResume)}
                        className="h-[40px] rounded-[8px] border border-[#CBD5E1] bg-white px-[16px] text-[14px] font-semibold text-[#334155] disabled:opacity-50"
                    >
                        {isResuming ? '해제하는 중' : '풀고 등록'}
                    </button>
                    <button
                        type="button"
                        disabled={isResuming}
                        onClick={guarded(onKeep)}
                        className="h-[40px] rounded-[8px] bg-[#3D73DD] px-[16px] text-[14px] font-semibold text-white disabled:opacity-50"
                    >
                        그대로 등록
                    </button>
                </div>
            </div>
        </Modal>
        </div>,
        document.body,
    );
};

export default ModalPauseCreateConfirm;
